import React, { useMemo } from 'react';
import { I18nextProvider, useTranslation } from 'react-i18next';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { i18n } from './main';
import { MapViewProvider, Map } from '@origo/map-context';
import { useAuthenticatedApp, useTokenRegistration } from '@origo/auth';
import { Button, Dialog, Loader, ToastProvider } from '@origo/origo-ui';
import AppLayout from './components/AppLayout';
import { useBackGuard } from './hooks/useBackGuard';
import { AccessDeniedScreen, useAccessErrorScreen } from '@origo/origo-components';
import {
    ArealplanClientProvider,
    createArealplanClient,
    type PlanTypeInfo,
} from '@origo/arealplan';
import { SeksjonClientProvider, createSeksjonClient } from '@origo/seksjon';
import { PlanMetadataServiceProvider } from './servicecontexts/PlanMetadataServiceContext';
import { KommunaltGalleriServiceProvider } from './servicecontexts/KommunaltGalleriServiceContext';
import { DROMMEPLAN_AGOL_GROUP } from './config/access';
import defaultSettings from './config/defaultSettings.json';

// One QueryClient for the app lifetime. Default retry policy keeps queries
// resilient to transient ArcGIS 5xx but avoids retry storms on mutations.
const queryClient = new QueryClient({
    defaultOptions: {
        queries: { retry: 2, staleTime: 30_000 },
        mutations: { retry: 0 },
    },
});


interface AppSettings {
    webmap?: string;
    webscene?: string;
    app?: {
        enableChat?: boolean;
    };
    planTypes?: Record<string, PlanTypeInfo>;
    seksjonUrl?: string;
    områderUrl?: string;
    teigerUrl?: string;
    seksjonElementUrl?: string;
    eiendommerUrl?: string;
    planMetadataUrl?: string;
    kommunaltGalleriUrl?: string;
    kommunenavn?: string;
    /** Søkefilter beregnet av rollouten, f.eks. `{ filter_kommunenr: "3107" }`. */
    sokefilter?: Record<string, string>;
}

const App: React.FC = () => {
    const { t } = useTranslation();
    const { token, settings, isReady, signOut, accessError } = useAuthenticatedApp<AppSettings>(
        'drømmeplan-admin',
        defaultSettings as AppSettings,
        { requiredGroup: DROMMEPLAN_AGOL_GROUP }
    );
    // Register GDO token in ArcGIS IdentityManager
    // Must be called in App to ensure token is registered before map layers load
    useTokenRegistration(token, 'https://services.geodataonline.no/arcgis/rest/services');

    const { showBackWarning, handleGoBack, handleStay } = useBackGuard(isReady);
    const accessErrorScreen = useAccessErrorScreen(accessError, signOut, {
        group: {
            title: 'Ingen tilgang',
            message:
                'Manglende tilgang kan skyldes at brukeren din ikke er medlem av organisasjonen i ArcGIS Online. Alternativt kan problemet være at brukeren din ikke er medlem i Drømmeplan-gruppa i ArcGIS Online. Ta kontakt med admin. ',
        },
        notFound: {
            title: 'Ingen tilgang',
            message: 'Kunne ikke finne innstillinger for kommunen. Ta kontakt med administrator.',
        },
    });

    // Veiledningspunkter knyttes kun til gjeldende planer — UA-varianter
    // (planer under arbeid) skal ikke kunne velges i plansøket. Filtreres
    // pre-fetch slik at fanout aldri spør UA-tjenestene.
    const gjeldendePlanTypes = useMemo(
        () =>
            Object.fromEntries(
                Object.entries(settings?.planTypes ?? {}).filter(
                    ([key]) => !key.includes('_UA')
                )
            ),
        [settings?.planTypes]
    );

    const arealplanClient = useMemo(() => {
        if (!isReady || !settings) return null;
        return createArealplanClient({
            settings: {
                planTypes: gjeldendePlanTypes,
                teigerUrl: settings.teigerUrl,
            },
        });
    }, [isReady, settings, gjeldendePlanTypes]);

    const seksjonClient = useMemo(() => {
        if (!isReady || !settings) return null;
        return createSeksjonClient({
            seksjonUrl: settings.seksjonUrl ?? '',
            områderUrl: settings.områderUrl,
            seksjonelementUrl: settings.seksjonElementUrl,
            eiendommerUrl: settings.eiendommerUrl,
        });
    }, [isReady, settings]);

    if (accessErrorScreen) {
        return <AccessDeniedScreen {...accessErrorScreen} />;
    }

    if (!isReady || !arealplanClient || !seksjonClient) {
        return (
            <div style={{
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                height: '100vh',
                flexDirection: 'column',
                gap: '1rem'
            }}>
                <Loader />
            </div>
        );
    }

    return (
        <ToastProvider position="bottom-right">
            <QueryClientProvider client={queryClient}>
                <ArealplanClientProvider client={arealplanClient}>
                    <SeksjonClientProvider client={seksjonClient}>
                        <PlanMetadataServiceProvider settings={{ planMetadataUrl: settings?.planMetadataUrl ?? "", seksjonUrl: settings?.seksjonUrl ?? "" }}>
                            <KommunaltGalleriServiceProvider settings={{ serviceUrl: settings?.kommunaltGalleriUrl ?? "" }}>
                                <I18nextProvider i18n={i18n}>
                                    <MapViewProvider>
                                        <AppLayout
                                            onSignOut={() => signOut()}
                                            eiendommerUrl={settings?.eiendommerUrl}
                                            kommunenavn={settings?.kommunenavn}
                                            sokefilter={settings?.sokefilter}
                                            mapContent={
                                                <Map
                                                    webMapId={settings?.webmap}
                                                    webSceneId={settings?.webscene}
                                                    tools2D={["fullscreen", "scalebar", "measure"]}
                                                    disableClick={false}
                                                    disablePopup={true}
                                                    disable3D={true}
                                                    includeSpesielleGrenserLayer={true}
                                                />
                                            }
                                        />
                                    </MapViewProvider>
                                    <Dialog
                                        open={showBackWarning}
                                        onClose={handleStay}
                                        title={t('admin.backWarning.title')}
                                        footer={
                                            <>
                                                <Button variant="danger" onClick={handleGoBack}>
                                                    {t('admin.backWarning.goBack')}
                                                </Button>
                                                <Button variant="secondary" onClick={handleStay}>
                                                    {t('admin.backWarning.stay')}
                                                </Button>
                                            </>
                                        }
                                    >
                                        <p>{t('admin.backWarning.message')}</p>
                                    </Dialog>
                                </I18nextProvider>
                            </KommunaltGalleriServiceProvider>
                        </PlanMetadataServiceProvider>
                    </SeksjonClientProvider>
                </ArealplanClientProvider>
            </QueryClientProvider>
        </ToastProvider>
    );
};

export default App;
